import { GridColumn, GridColumnIcon } from '@glideapps/glide-data-grid'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { ColumnAdapter } from '@makyoapp/makyo-common'

import { StorageUtil } from '@/lib/storage'

export type ExtendedGridColumn = GridColumn & {
  isHidden: boolean
  isFrozen: boolean
  width?: number
}

/**
 * Parses a list of ColumnAdapter objects into ExtendedGridColumn objects
 */
export function parseGlideColDefList(
  cols: ColumnAdapter[],
  prev: ExtendedGridColumn[],
  isSnapshotOrQuery: boolean,
  isQueryTable: boolean
): ExtendedGridColumn[] {
  // Find the index of the first visible column
  const firstVisibleColumnIndex = cols.findIndex((col) => !col.isHidden)

  return cols.map((col, idx) => {
    let prevWidth = prev.find((c) => c.id === col.id)?.width

    const isSpecialLocked =
      ['timestamp', 'email'].includes((col.name || '').toLowerCase()) ||
      col.type === ColumnTypes.RowID ||
      col.type === ColumnTypes.SessionID ||
      col.type === ColumnTypes.Email

    return {
      id: col.id,
      title: col.name,
      hasMenu: true,
      icon: col.type,
      isHidden: col.isHidden,
      isFrozen: col.isFrozen,
      // @ts-ignore
      width: col.width ?? prevWidth,
      trailingRowOptions: {
        disabled: isSnapshotOrQuery || idx !== firstVisibleColumnIndex, // only show `Add New Row` on the first visible column
      },
      style: col.isSourceColumn ? 'highlight' : 'normal',
      overlayIcon:
        (isSnapshotOrQuery && col.isSourceColumn) || isSpecialLocked
          ? GridColumnIcon.ProtectedColumnOverlay
          : undefined,
      ...(isQueryTable && {
        group: col.isSourceColumn ? 'Source Columns' : 'Original Columns',
      }),
    }
  })
}

// ====================================================================================
// Helpers to manage cached glide columns (for resizing)                             //
// ====================================================================================

const STORAGE_NAME = 'GRID_COLUMNS'
const CACHE_EXPIRY_DAYS = 3

const storage = new StorageUtil()

export function getCachedColumnsData(): Record<string, ExtendedGridColumn[]> {
  storage.removeExpiredItems()
  return storage.getItem<Record<string, ExtendedGridColumn[]>>(STORAGE_NAME) || {}
}

export function setCachedColumnData(data: Record<string, ExtendedGridColumn[]>) {
  storage.setItem(STORAGE_NAME, data, CACHE_EXPIRY_DAYS)
}

export function removeCachedColumnData() {
  storage.clear()
}
